/* eslint-disable react/prop-types */
import React, { Fragment } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Link from 'next/link';

import PageHeader from '../tissues/page-header';
import Typography from '../atoms/typography';

import { getBasePath } from '../utils/navigation';
import getInnerTextFromString from '../utils/getInnerTextFromString';
import { getOverrideMeta } from '../utils/metaOverrides';
import SLIHyphenMap from '../utils/SLIHyphenMap.json';

const HeaderText = styled(Typography)`
  text-transform: capitalize;
`;

const HeaderLink = styled(HeaderText)`
  color: ${(props) => props.theme.color.brand};
  transition: color ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuad};
  text-decoration: none;

  &:hover {
    color: ${(props) => props.theme.color.brandLight};
  }
`;

const SubHeader = styled(Typography)`
  color: ${(props) => props.theme.color.brandLight};
  letter-spacing: inherit;
  text-decoration: none;
`;

const SubHeaderLink = styled(SubHeader)`
  color: ${(props) => props.theme.color.brand};
  transition: color ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuad};

  &:hover {
    color: ${(props) => props.theme.color.brandLight};
  }
`;

const CorrectedFrom = styled(Typography)`
  color: ${(props) => props.theme.color.brandLight};
  margin-left: 0.5rem;
  display: inline-block;
`;

const PLPPageHeader = (props) => {
  const { pathname } = useRouter();

  let page = pathname.split('/')[1];
  let header = props.headers?.header?.name ?? null;

  if (props.lpc) {
    page = 'lpc';
    header = getInnerTextFromString(props.merchHeader);
  }

  if (props.edit) {
    page = 'edits';
    header = props.editName;
  }

  if (props.trend) {
    page = 'trends';
    header = getOverrideMeta(null, 'trend', props)?.heading ?? props.trendName;
  }

  if (props.brand) {
    page = 'brands';
  }

  if (props.noResults) {
    const subHeading = props.trendName
      ? 'No Results'
      : props.correctedSearchTerm && (props.correctedSearchTerm !== props.searchTerm) && (
        <>
          {'corrected from '}
          <CorrectedFrom element="span" like="label-1">
            {props.correctedSearchTerm}
          </CorrectedFrom>
        </>
      );

    return (
      <PageHeader
        subHeading={subHeading}
        heading={
          props.searchTerm === '*'
            ? header || props.trendName || 'No Results'
            : `Search: ${props.searchTerm}`
        }
      />
    );
  }

  const [w] = props.searchTerm.split('/');
  if (page === 'shop' && w !== '*') {
    const subHeading = props.correctedSearchTerm && w !== props.correctedSearchTerm
      ? (
        <>
          {'corrected from '}
          <CorrectedFrom element="span" like="label-1">{props.correctedSearchTerm}</CorrectedFrom>
        </>
      ) : null;

    return w ? <PageHeader subHeading={subHeading} heading={`Search: ${w}`} /> : null;
  }

  if (page === 'shop' || page === 'products') {
    const filters = props?.filters?.map((x) => x.split(':')?.[1] ?? null).filter((x) => x);
    const category = filters ? filters[filters.length - 1] : null;

    return props.headers?.header ? (
      <PageHeader
        heading={getOverrideMeta(category, 'shop', props)?.heading ?? props.headers.header.name}
        subHeading={
          props.headers?.subHeaders.length > 0
          && props.headers?.subHeaders?.map((subHeader, i) => {
            const breadcrumbs = subHeader.id.split('_').map((x) => SLIHyphenMap[x] ?? x);

            return (
              <Fragment key={`header-breadcrumb-${subHeader.id}`}>
                <Link
                  href={`${getBasePath(props)}/${breadcrumbs.join('/')}`}
                  passHref
                >
                  <SubHeaderLink element="a" like="label-1">
                    {subHeader.name}
                  </SubHeaderLink>
                </Link>
                {i !== props.headers.subHeaders.length - 1 ? ' | ' : ''}
              </Fragment>
            );
          })
        }
      />
    ) : null;
  }

  if (page === 'brands') {
    const { heading } = getOverrideMeta([], 'brand', props);

    return (
      <PageHeader
        heading={heading}
        subHeading="Brand"
        text={props?.brand?.description ?? ''}
        image={props?.brand?.icon ?? ''}
        brandFilters={props?.brand?.permalink === 'brands/maison-me' ? [
          ['Baby', { cats: ['baby'] }],
          ['Kids', { cats: ['kids'] }],
          ['Adult', { af: ['agerange:adults'] }],
          ['Shop All', {}]
        ] : null}
      />
    );
  }

  return (
    <PageHeader
      heading={
        props.headers?.header ? (
          <Link
            href={getBasePath(props)}
            passHref
          >
            <HeaderLink element="a" like="heading-2">{header}</HeaderLink>
          </Link>
        ) : <HeaderText element="span" like="heading-2">{header}</HeaderText>
      }
      subHeading={(props.headers?.subHeaders.length > 0 || props.headers?.header) && (
        <>
          {
            props.headers?.subHeaders.length > 0
            && props.headers?.subHeaders?.map((subHeader) => {
              const breadcrumbs = subHeader.id.split('_').map((x) => SLIHyphenMap[x] ?? x);

              return (
                <Fragment key={`header-breadcrumb-${subHeader.id}`}>
                  <Link
                    href={`${getBasePath(props)}/${breadcrumbs.join('/')}`}
                    passHref
                  >
                    <SubHeaderLink element="a" like="label-1">
                      {subHeader.name}
                    </SubHeaderLink>
                  </Link>
                  {' | '}
                </Fragment>
              );
            })
          }

          {
            props.headers?.header && (
              <SubHeader element="span" like="label-1">
                {props.headers.header.name}
              </SubHeader>
            )
          }
        </>
      )}
    />
  );
};

PLPPageHeader.defaultProps = {
  brand: null,
  correctedSearchTerm: null,
  edit: null,
  headers: null,
  lpc: null,
  merchHeader: null,
  noResults: false,
  searchTerm: '*',
  trend: null,
  trendName: null
};

PLPPageHeader.propTypes = {
  brand: PropTypes.object,
  correctedSearchTerm: PropTypes.string,
  edit: PropTypes.string,
  headers: PropTypes.object,
  lpc: PropTypes.string,
  merchHeader: PropTypes.string,
  noResults: PropTypes.bool,
  searchTerm: PropTypes.string,
  trend: PropTypes.string,
  trendName: PropTypes.string
};

PLPPageHeader.whyDidYouRender = true;

export default PLPPageHeader;
